import type { ReactElement } from "react";
import React, { createRef, useEffect, useMemo } from "react";
import { RiFullscreenLine } from "react-icons/ri";
import { Unity, useUnityContext } from "react-unity-webgl";
import { isBrowserSupportingWebGL } from "../../../common/util/isBrowserSupportingWeb";
import UnsupportedWebGL from "../../../common/components/UnsupportedWebGL/UnsupportedWebGL";
import { useTranslation } from "react-i18next";
import ProgressBar from "../../../common/components/ProgressBar/ProgressBar";

const viewerFolder = "/Model3dViewerWeb/";

interface Props {
  modelUrl: string;
  className?: string;
  canvasClassName?: string;
  disableFullScreen?: boolean;
}

export default function Model3dViewerWeb({
  modelUrl,
  className,
  canvasClassName,
  disableFullScreen = false,
}: Props): ReactElement {
  const [isLoading, setIsLoading] = React.useState(true);
  const isSupportingWebGL = useMemo(() => isBrowserSupportingWebGL(), []);
  const canvasRef = createRef<HTMLCanvasElement>();
  const { t } = useTranslation();

  const {
    unityProvider,
    sendMessage,
    isLoaded,
    loadingProgression,
    UNSAFE__detachAndUnloadImmediate,
  } = useUnityContext({
    loaderUrl: viewerFolder + "Build/Model3dViewerWeb.loader.js",
    dataUrl: viewerFolder + "Build/Model3dViewerWeb.data",
    frameworkUrl: viewerFolder + "Build/Model3dViewerWeb.framework.js",
    codeUrl: viewerFolder + "Build/Model3dViewerWeb.wasm",
  });

  const configureUnityApp = () => {
    sendMessage("AssetViewer", "LoadModelFromURLWithUrl", modelUrl);

    canvasRef.current?.setAttribute("tabindex", "1");
    canvasRef.current?.focus();

    setIsLoading(false);
  };

  useEffect(() => {
    if (isLoaded) {
      configureUnityApp();
    }
  }, [isLoaded]);

  // Cleanup Unity instance on unmount using synchronous immediate unload
  useEffect(() => {
    return () => {
      void UNSAFE__detachAndUnloadImmediate();
    };
  }, [UNSAFE__detachAndUnloadImmediate]);

  const toggle = () => {
    const elem = document.querySelector("#div-unity");

    if (!document.fullscreenElement) {
      elem?.requestFullscreen().catch((err) => {
        alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
      });
    } else {
      void document.exitFullscreen();
    }
  };

  return (
    <div className={className ?? ""}>
      <div
        className={`w-full h-full flex flex-col justify-center items-center ${
          isLoading ? "" : "hidden"
        }`}
      >
        <p>{t("general.loading")}</p>
        <ProgressBar
          wrapperClassName="w-1/4 flex bg-gray-500 h-[3%]"
          barClassName="bg-gray-900"
          value={loadingProgression * 100}
        />
      </div>
      <div
        id="div-unity"
        className={`h-full ${isLoading ? "hidden" : "flex"} flex-col items-center`}
      >
        {isSupportingWebGL ? (
          <>
            <Unity
              unityProvider={unityProvider}
              ref={canvasRef}
              className={`${isLoading ? "hidden" : "visible"} ${canvasClassName ?? ""}`}
              style={{ height: "100%", width: "100%" }}
            />
          </>
        ) : (
          <UnsupportedWebGL />
        )}
        {isSupportingWebGL && !disableFullScreen && (
          <button
            className={`relative mr-2 mt-[-3rem] self-end btn-fullscreen ${
              isLoading ? "hidden" : "visible"
            }`}
            onClick={() => {
              toggle();
            }}
          >
            <RiFullscreenLine fill="white" size={40} />
          </button>
        )}
      </div>
    </div>
  );
}
